import ConsumerRoutePath from 'app/consumer-app/ConsumerRoutePath';
import { useSetPageTitle } from 'app/shared/context-hooks/use-titlebar.hook';
import ClassName from 'app/styles/ClassName';
import Icon from 'app/shared/components/icon/Icon';
import IconLib from 'app/shared/components/icon/icon-lib';
import Button from 'app/shared/components/button/Button';
import { PropsOf } from 'app/tools/general.types';
import clsx from 'clsx';
import React from 'react';
import { getErrorMsg } from 'app/core/errors/error-utils';
import { useHistory } from 'react-router-dom';
import { Merge } from 'type-fest';
import {
  useDeletePaymentMethod,
  useGetPaymentMethod,
} from 'app/core/api/user/api-user';
import { PaymentMethod } from 'app/core/api/repairer/api-repairer.types';
import { useShowInfo } from 'app/shared/context-hooks/use-show-info.hook';

const SectionBox: React.FC<{ title: string }> = ({
  title,
  children,
}) => (
  <>
    <h3
      className={clsx(
        'mt-1 mb-0 font-normal text-primary',
        ClassName.NO_HEIGHT_MARGIN
      )}
    >
      {title}
    </h3>
    <div className="flex flex-col rounded border border-solid border-neutral-200">
      {children}
    </div>
  </>
);

const SectionButton: React.VFC<
  Merge<
    PropsOf<typeof Button>,
    { title: string; subtitle: string; icon: IconLib }
  >
> = ({ title, subtitle, icon, ...buttonProps }) => (
  <Button variant="text" className="flex-grow h-btn" {...buttonProps}>
    <Icon {...{ icon }} />
    <div className="flex-grow ml-1">
      <h4 className="m-0 mb-0.2 text-sm font-semibold text-left line-clamp-1">
        {title}
      </h4>
      <div className="text-xs font-light text-left line-clamp-2">
        {subtitle}
      </div>
    </div>
    <Icon icon={IconLib.CHEVRON_RIGHT} />
  </Button>
);

type Props = {
  firstTime?: boolean;
};

export default function Wallet({
  firstTime = false,
}: Props): JSX.Element {
  useSetPageTitle('Wallet');
  const history = useHistory();
  // const cardId = +useUrlParams(['cardId']).id;

  const { resource: cardData, request: refresh } =
    useGetPaymentMethod();
  const { request$: deleteAssignedCard$ } = useDeletePaymentMethod();
  const { showDialog, showSnack } = useShowInfo();

  const handleDeleteSuccess = () => {
    showSnack({
      message: 'Card deleted',
      alertType: 'error',
    });
    history.replace(ConsumerRoutePath.WALLET);
    refresh();
  };

  const handleDeleteError = (e: unknown) => {
    showSnack({ message: getErrorMsg(e), alertType: 'error' });
  };

  const deleteConfirmCallback = (key: string) =>
    deleteAssignedCard$(key).subscribe({
      next: handleDeleteSuccess,
      error: handleDeleteError,
    });

  const handleDeleteRequest = (key: string) =>
    showDialog({
      title: 'Delete Card',
      message: 'Are you sure you want to delete this card?',
      actions: [
        { text: 'Cancel' },
        {
          text: 'Confirm Delete',
          callback: () => {
            deleteConfirmCallback(key);
          },
        },
      ],
    });

  const CardButton: React.VFC<
    Merge<
      PropsOf<typeof Button>,
      {
        title: string;
        subtitle: string;
        subsubtitle: string;
        icon: IconLib;
        key: string;
        paymentMethodId: string;
      }
    >
  > = ({
    title,
    subtitle,
    subsubtitle,
    icon,
    key,
    paymentMethodId,
    ...buttonProps
  }) => (
    <Button
      variant="text"
      className="flex-grow h-btn"
      {...buttonProps}
    >
      <Icon {...{ icon }} />
      <div className="flex-grow ml-1">
        <h5 className="m-0 mb-0.2 text-sm font-semibold text-left line-clamp-1">
          {title}
        </h5>
        <div className="text-xs font-light text-left line-clamp-2">
          {subtitle}
        </div>
        <div className="text-xs font-light text-left line-clamp-2">
          ****-****-****-{subsubtitle}
        </div>
      </div>
      <Icon
        icon={IconLib.TRASH}
        onClick={() => handleDeleteRequest(paymentMethodId)}
      />
    </Button>
  );

  return (
    <>
      {firstTime ? (
        <SectionBox title="Add Payment Method">
          <SectionButton
            title="Add Payment Method"
            subtitle="Add or remove payment options"
            to={ConsumerRoutePath.ADD_CARD}
            icon={IconLib.ADD_NEW}
          />
        </SectionBox>
      ) : (
        <>
          <SectionBox title="Payment Details">
            {cardData &&
              cardData.data.map((val: PaymentMethod) => (
                <CardButton
                  style={{ marginTop: '5px' }}
                  key={val.paymentMethodId}
                  paymentMethodId={val.paymentMethodId}
                  title={val.nameOnCard}
                  subtitle={val.brand}
                  subsubtitle={val.last4}
                  icon={IconLib.REWARD_COUPON}
                />
              ))}
            <SectionButton
              title="History"
              subtitle="History of all transactions"
              to={ConsumerRoutePath.HISTORY}
              icon={IconLib.PIE_CHART}
            />
          </SectionBox>

          <SectionBox title="Payment Method">
            <SectionButton
              title="Add Payment Method"
              subtitle="Add or remove payment options"
              to={ConsumerRoutePath.ADD_CARD}
              icon={IconLib.ADD_NEW}
            />
          </SectionBox>
        </>
      )}
    </>
  );
}
